import mqtt from "precompiled-mqtt";

class MqttClass {
    constructor(brokerUrl, host = "", port = "", protocol = 'mqtt', automaticReconnect) {
        if (brokerUrl && brokerUrl !== '') {
            this.broker = brokerUrl;
        } else if (host !== "" && port !== "") {
            this.broker = new URL(`${protocol}://${host}:${port}`).toString();
        }
        this.host = host;
        this.port = port;
        this.automaticReconnect = automaticReconnect || false;
        this.mqttClient = null;

        this.onMessage = null;

        this.connectingIntervalActive = false;
        this.connectingIntervalTimeout = null;
        this.connectingIntervalTimeoutDuration = 60000;  // 1 minute in ms

        this.initialMessageTimeoutDuration = 2000; //wait 2 seconds for initialMessage

        this.offlineTopicSubscriptions = new Map();
    }

    establishConnection() {
        if (this.onMessage) {
            this.onMessage({ action: 'MQTT_STATE', payload: 'connecting' });
        }
        this._close();
        this._connect();
        this.connectingIntervalActive = true;
        this.connectingIntervalTimeout = setTimeout(() => {
            this.connectingIntervalActive = false;
            this._close();
        }, this.connectingIntervalTimeoutDuration);
    }

    //Publish MQTT Message with default options: QoS 1 and retain-flag = false.
    publish(topic, message, options) {
        //console.log("Publish recieved: ", topic, message);
        if (!options) {
            options = { qos: 1, retain: false };
        }
        if (this.mqttClient && this.mqttClient.connected) {
            this.mqttClient.publish(topic, message, options);
            //console.log("Message:", message, "published to Broker:", this.data.broker, " on topic:", topic);
        } else {
            console.error("Error while publishing: No connection with the Broker: ", this.broker, "!");
        }
    }

    //Subscribe to MQTT Message with dafault options: QoS 1.
    subscribe(topic, options) {
        if (!options) {
            options = { qos: 1 };
        }
        if (this.mqttClient && this.mqttClient.connected) {
            this.mqttClient.subscribe(topic, options);
            if (this.offlineTopicSubscriptions.has(topic)) {
                this.offlineTopicSubscriptions.delete(topic);
            }
            //console.log("Subscribed by the Broker:", this.data.broker, " the topic:", topic);
        } else {
            if (!this.offlineTopicSubscriptions.has(topic)) {
                this.offlineTopicSubscriptions.set(topic, options);
            }
            console.info("Subscription: ", topic, " got remembered as offlineSubscription.")
            // console.error("Error while subscribing: No connection with the Broker: ", this.broker, "!");
        }
    }

    subscribeOfflineTopicSubscriptions() {
        if (this.offlineTopicSubscriptions.size > 0) {
            for (const [topic, options] of [...this.offlineTopicSubscriptions]) {
                this.subscribe(topic, options);
            }
            // console.log("Offline Topics got subscribed!");
        }
    }

    //Subscribe to MQTT Message with dafault options: QoS 1.
    unsubscribe(topic) {
        if (this.mqttClient && this.mqttClient.connected) {
            this.mqttClient.unsubscribe(topic);
            //console.log("Unsubscribed by the Broker:", this.data.broker, " the topic:", topic);
        } else {
            console.error("Error while unsubscribing: No connection with the Broker: ", this.broker, "!");
        }
    }

    cleanup() {
        this._close();
        this._clearInitialMessageTimeout();
        this._clearConnectingIntervalTimeout();
    }

    _clearConnectingIntervalTimeout() {
        if (this.connectingIntervalTimeout !== null && this.connectingIntervalTimeout !== undefined) {
            clearTimeout(this.connectingIntervalTimeout);
            this.connectingIntervalTimeout = null;
            this.connectingIntervalActive = false;
        }
    }

    _clearInitialMessageTimeout() {
        if (this.initialMessageTimeout !== null && this.initialMessageTimeout !== undefined) {
            clearTimeout(this.initialMessageTimeout);
            this.initialMessageTimeout = null;
        }
    }

    _close() {
        if (this.mqttClient) {
            this.mqttClient.end();
            this.mqttClient = null;
        }
    }

    //InitialSubscribe is executed automatically with every instantiation and subscribes to the initial_data of the broker 
    //in order to obtain all the necessary information about the associated sensors.
    async _initialSubscribe() {
        if (this.mqttClient && this.mqttClient.connected) {
            //In this subscription "/#" means that all subtopics are also subscribed.
            this.mqttClient.subscribe("initial_data/#", { qos: 1 });
            //console.log("Subscribed Initial Data from Broker:", this.data.broker);
            this.initialMessageTimeout = setTimeout(() => {
                console.error("Timeout: No initial_data received within 2 seconds!");
            }, this.initialMessageTimeoutDuration);
        }
    }

    _connect() {
        this.mqttClient = mqtt.connect(this.broker);

        this.mqttClient.on('connect', () => {
            if (this.onMessage) {
                this.onMessage({ action: 'MQTT_STATE', payload: 'connected' });
            }
            this._clearConnectingIntervalTimeout();
            this._initialSubscribe();
            this.subscribeOfflineTopicSubscriptions();
            console.log("Connected to the MQTT broker: ", this.broker);

        });

        this.mqttClient.on('offline', () => {
            if (!this.connectingIntervalActive) {
                if (this.onMessage) {
                    this.onMessage({ action: 'MQTT_STATE', payload: 'offline' });
                }
                console.log("The MQTT broker: ", this.broker, " is offline!");
            }
        });

        this.mqttClient.on('close', () => {
            if (!this.connectingIntervalActive) {
                if (this.onMessage) {
                    this.onMessage({ action: 'MQTT_STATE', payload: 'closed' });
                }
                console.log("The MQTT broker: ", this.broker, " is closed!");
                if (this.automaticReconnect) {
                    this.establishConnection();
                }
            }
        });

        //the "error" listener is triggered if an error occurs during communication or connection setup and sets the status accordingly.
        this.mqttClient.on('error', (error) => {
            if (!this.connectingIntervalActive) {
                if (this.onMessage) {
                    this.onMessage({ action: 'MQTT_STATE', payload: 'error' });
                }
                console.error("An error occurred during the communication!\n" + error);
            }
        });

        //The "message" listener is executed when a message is received (e.g. based on a subscribed topic) and decides on the further processing of the message.
        this.mqttClient.on('message', (topic, message) => {
            //When a message arrives from the broker, it is checked whether the message is related to the initial_data request or another request.
            //When the initial_data case occurs, the initialMessageTimout is deleted and the sensorsConnected and initial_data are updated.
            if (!message) {
                console.error("The received message does not contain any valid data!");
                return;  // Keine gültige Nachricht, Rückkehr
            }
            try {
                const parsedMessage = JSON.parse(message); // Sichere Verarbeitung der Nachricht

                if (topic.startsWith("initial_data")) {
                    this._clearInitialMessageTimeout();
                    // console.info(`Received initial_data from broker ${this.broker} on topic ${topic}`);
                    if (this.onMessage) {
                        this.onMessage({ action: 'UPDATE_INITIAL_DATA', payload: { broker: this.broker, message: message } });
                    }
                } else {
                    // console.debug(`Received message on topic ${topic}:`, parsedMessage);
                    if (this.onMessage) {
                        this.onMessage({ action: 'ON_MESSAGE', topic: topic, payload: parsedMessage });
                    }
                }
            }
            catch (error) {
                console.error("Error parsing the received message:", error);
            }
        });
    }
};

class MQTTConnectionManager {
    constructor() {
        this.mqttConnections = new Map();
        this.topicSubscriptions = new Map();
    }

    handleMessage(event) {
        const { action, payload } = event.data;

        switch (action) {
            case 'CONNECT':
                this.connect(payload.brokerUrl, payload.host, payload.port, payload.automaticReconnect, payload.componentId);
                break;
            case 'RECONNECT':
                this.reconnect(payload.brokerUrl, payload.host, payload.port);
                break;
            case 'PUBLISH':
                this.publish(payload.brokerUrl, payload.host, payload.port, payload.topic, payload.message, payload.options);
                break;
            case 'SUBSCRIBE':
                this.subscribe(payload.brokerUrl, payload.host, payload.port, payload.topic, payload.componentId, payload?.options);
                break;
            case 'UNSUBSCRIBE':
                this.unsubscribe(payload.brokerUrl, payload.host, payload.port, payload.topic, payload.componentId);
                break;
            case 'DISCONNECT':
                this.disconnect(payload.brokerUrl, payload.host, payload.port, payload.componentId);
                break;
            default:
                console.error('MqttConnectionManager unknown action:', action);
                break;
        }
    }

    // Verbindung herstellen oder wiederverwenden
    connect(brokerUrl, host, port, automaticReconnect, componentId) {
        var url = "mqtt://" + host + ":" + port;
        if (brokerUrl && brokerUrl !== '') {
            url = brokerUrl;
        }
        // console.log('mqttConnectionManager recieved connect: \nbrokerUrl: ' + brokerUrl + '\nhost:' + host + '\nport:' + port + '\nautomaticReconnect:' + automaticReconnect + '\ncomponentId:' + componentId);
        if (this.mqttConnections.has(url)) {
            // Verbindung existiert bereits, Referenzzähler erhöhen 
            const connection = this.mqttConnections.get(url);
            connection.componentIds.add(componentId);
            return;
        } else {
            // Neue Verbindung erstellen
            const mqttInstance = new MqttClass(brokerUrl, host, port, automaticReconnect);
            mqttInstance.establishConnection();
            this.mqttConnections.set(url, {
                mqttInstance: mqttInstance,
                componentIds: new Set([componentId]),
            });
            mqttInstance.onMessage = ({ action, topic = "system", payload }) => {
                if (topic !== "system") {
                    const subscribers = this.topicSubscriptions.get(topic);
                    if (subscribers && subscribers.size > 0) {
                        postMessage({
                            url: url,
                            action: action,
                            topic: topic,
                            subscriberComponentIds: Array.from(subscribers),
                            payload: payload
                        });
                    }
                    else {
                        console.error("Keinen Subscriber der Nachricht!\nTODO: subscribe/unsubscribe prüfen!");
                    }
                } else {
                    postMessage({ url: url, action: action, topic: topic, payload: payload });
                }
            }
            return;
        }
    }

    //Verbindung wiederaufbauen
    reconnect(brokerUrl, host, port) {
        var url = "mqtt://" + host + ":" + port;
        if (brokerUrl && brokerUrl !== '') {
            url = brokerUrl;
        }
        if (this.mqttConnections.has(url)) {
            const mqttInstance = this.mqttConnections.get(url).mqttInstance;
            mqttInstance.cleanup();
            mqttInstance.establishConnection();
        }
    }

    // Nachricht über eine Verbindung senden
    publish(brokerUrl, host, port, topic, message, options) {
        var url = "mqtt://" + host + ":" + port;
        if (brokerUrl && brokerUrl !== '') {
            url = brokerUrl;
        }
        if (this.mqttConnections.has(url)) {
            this.mqttConnections.get(url).mqttInstance.publish(topic, message, options);
        } else {
            console.error(`Keine MQTT-Verbindung für die URL: ${url}`);
        }
    }

    subscribe(brokerUrl, host, port, topic, componentId, options) {
        var url = "mqtt://" + host + ":" + port;
        if (brokerUrl && brokerUrl !== '') {
            url = brokerUrl;
        }
        if (this.mqttConnections.has(url)) {
            this.mqttConnections.get(url).mqttInstance.subscribe(topic, options);
            if (!this.topicSubscriptions.has(topic)) {
                this.topicSubscriptions.set(topic, new Set());
            }
            this.topicSubscriptions.get(topic).add(componentId);
        } else {
            console.error(`Keine MQTT-Verbindung für die URL: ${url}`);
        }
    }

    unsubscribe(brokerUrl, host, port, topic, componentId) {
        var url = "mqtt://" + host + ":" + port;
        if (brokerUrl && brokerUrl !== '') {
            url = brokerUrl;
        }
        if (this.mqttConnections.has(url)) {
            this.mqttConnections.get(url).mqttInstance.unsubscribe(topic);
            if (this.topicSubscriptions.has(topic)) {
                this.topicSubscriptions.get(topic).delete(componentId);
                if (this.topicSubscriptions.get(topic).size === 0) {
                    this.topicSubscriptions.delete(topic);
                }
            }
        } else {
            console.error(`Keine MQTT-Verbindung für die URL: ${url}`);
        }
    }

    // Verbindung trennen
    disconnect(brokerUrl, host, port, componentId) {
        var url = "mqtt://" + host + ":" + port;
        if (brokerUrl && brokerUrl !== '') {
            url = brokerUrl;
        }
        if (this.mqttConnections.has(url)) {
            const connection = this.mqttConnections.get(url);
            connection.componentIds.delete(componentId);
            if (connection.componentIds.size === 0) {
                // Keine weiteren Referenzen, Verbindung schließen
                connection.mqttInstance.cleanup();
                connection.mqttInstance = null;
                this.mqttConnections.delete(url);
            }
        }
    }
};

const mqttConnectionManager = new MQTTConnectionManager();

onmessage = function (event) {
    mqttConnectionManager.handleMessage(event);
};